/* One element per offer. Survives Shopify section replacement without global timers. */
if (!customElements.get('mf-discount-code')) {
  customElements.define('mf-discount-code', class extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-discount-copy]');
      this.code = this.querySelector('[data-discount-code]');
      this.status = this.querySelector('[data-discount-status]');
      if (!this.button || !this.code || !this.status) return;
      this.controller?.abort();
      this.controller = new AbortController();
      this.button.hidden = false;
      this.copying = false;
      this.resetCopy();
      this.button.addEventListener('click', () => this.copyCode(), { signal: this.controller.signal });
      this.startCountdown();
    }

    disconnectedCallback() {
      this.controller?.abort();
      clearInterval(this.countdownInterval);
      clearTimeout(this.resetTimeout);
    }

    resetCopy() {
      clearTimeout(this.resetTimeout);
      this.button.dataset.copied = 'false';
      this.button.querySelector('[data-discount-copy-text]').textContent = this.button.dataset.copyLabel;
      this.button.setAttribute('aria-label', `${this.button.dataset.copyLabel} ${this.code.textContent.trim()}`);
      this.status.textContent = '';
      this.status.classList.add('mf-discount__sr-only');
    }

    selectCode() {
      this.button.focus({ preventScroll: true });
      const selection = window.getSelection();
      const range = document.createRange();
      range.selectNodeContents(this.code);
      selection.removeAllRanges();
      selection.addRange(range);
    }

    legacyCopy() {
      const active = document.activeElement;
      const selection = window.getSelection();
      const ranges = Array.from({ length: selection.rangeCount }, (_, index) => selection.getRangeAt(index).cloneRange());
      this.selectCode();
      const copied = document.execCommand('copy');
      if (copied) {
        selection.removeAllRanges();
        ranges.forEach((range) => selection.addRange(range));
        active?.focus({ preventScroll: true });
      }
      return copied;
    }

    async copyCode() {
      if (this.copying || this.hidden) return;
      this.copying = true;
      this.resetCopy();
      const lifecycle = this.controller;
      let copied = false;
      try {
        if (navigator.clipboard?.writeText && window.isSecureContext) {
          await navigator.clipboard.writeText(this.code.textContent.trim());
          copied = true;
        }
      } catch (_) { /* Try selection-based copy when Clipboard API access is denied. */ }
      if (lifecycle.signal.aborted || !this.isConnected) return;
      if (!copied) {
        try { copied = this.legacyCopy(); } catch (_) { copied = false; }
      }
      this.copying = false;
      if (!copied) {
        this.selectCode();
        this.status.classList.remove('mf-discount__sr-only');
        this.status.textContent = this.button.dataset.copyError;
        return;
      }
      const label = this.button.dataset.copiedLabel;
      this.button.dataset.copied = 'true';
      this.button.querySelector('[data-discount-copy-text]').textContent = label;
      this.button.setAttribute('aria-label', `${label} ${this.code.textContent.trim()}`);
      this.status.textContent = `${label} ${this.code.textContent.trim()}`;
      this.resetTimeout = setTimeout(() => this.resetCopy(), 2600);
    }

    hideOffer(message) {
      clearInterval(this.countdownInterval);
      this.hidden = true;
      if (this.dataset.editor === 'true') {
        this.hidden = false;
        this.querySelector('[data-discount-body]').hidden = true;
        const note = this.querySelector('[data-discount-editor-note]');
        note.textContent = message;
        note.hidden = false;
      }
    }

    startCountdown() {
      clearInterval(this.countdownInterval);
      if (this.dataset.countdown !== 'true') {
        this.hidden = false;
        return;
      }
      const endAt = this.dataset.endAt.trim();
      const timestamp = Date.parse(endAt);
      // Explicit offsets keep every visitor on the same deadline. Never reset on reload.
      const parts = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(Z|[+-]\d{2}:\d{2})$/.exec(endAt);
      const [, year, month, day, hour, minute, second] = parts || [];
      const validCalendarDate = parts && Number(year) >= 1000 && Number(month) >= 1 && Number(month) <= 12
        && Number(day) >= 1 && Number(day) <= new Date(Date.UTC(Number(year), Number(month), 0)).getUTCDate()
        && Number(hour) < 24 && Number(minute) < 60 && Number(second) < 60;
      if (!validCalendarDate || !Number.isFinite(timestamp)) {
        this.hideOffer('Discount code: set a valid deadline including its time zone, or turn off the countdown.');
        return;
      }
      const timer = this.querySelector('[data-discount-timer]');
      const time = this.querySelector('[data-discount-time]');
      const update = () => {
        const remaining = timestamp - Date.now();
        if (remaining <= 0) {
          this.hideOffer('This discount offer has ended and is hidden from customers.');
          return false;
        }
        const seconds = Math.ceil(remaining / 1000);
        const days = Math.floor(seconds / 86400);
        const hours = Math.floor(seconds % 86400 / 3600);
        const minutes = Math.floor(seconds % 3600 / 60);
        const pad = (number) => String(number).padStart(2, '0');
        time.textContent = `${days ? `${days}d ` : ''}${pad(hours)}:${pad(minutes)}:${pad(seconds % 60)}`;
        time.setAttribute('aria-label', `${days} days, ${hours} hours, ${minutes} minutes remaining`);
        timer.hidden = false;
        this.hidden = false;
        return true;
      };
      if (update()) this.countdownInterval = setInterval(update, 1000);
    }
  });
}
